import { EditIcon, Trash2Icon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router';
import { ChannelType } from '../../../types/channelTypes';
import firebaseStore from '../../stores/firebaseStore';
import modalStore from '../../stores/modalStore';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { CommonTimeFormat } from '../common/CommonDateFormat';
import { useCommonNavigate } from '../common/CommonLink';
import { MarkdownViewer } from '../common/editor/MarkdownViewer';
import { ChannelProvider, useChannelStore } from './ChannelProvider';
import { PostProvider, usePostStore } from './PostProvider';
import { apiClient } from '../../clientUtils/apiClient';
import webViewStore from '../../stores/webViewStore';

const PostPageContent = observer(() => {
    const { channelType, postHash } = useParams();
    const { navigate, navigateBack } = useCommonNavigate();

    const channelStore = useChannelStore();
    const postStore = usePostStore();

    // 작성자이고 쓰기 권한이 있으면 수정 가능
    const canEdit = !!postStore.isAuthor && !!channelStore.canWrite;

    const isLoading = channelStore.isLoading || postStore.isLoading;

    return (
        <Frame>
            <Frame.Title
                title={postStore.post?.title}
                isLoading={isLoading}
                isShowMoreButton={canEdit || firebaseStore.isAdmin}
                onClickMoreButton={async () => {
                    await modalStore.menu({
                        title: '더보기',
                        items: [
                            {
                                name: '수정하기',
                                icon: <EditIcon className="size-4" />,
                                onSelect: () => {
                                    navigate(`/channel/${channelType}/editor?postHash=${postHash}`);
                                },
                            },
                            {
                                name: '삭제',
                                icon: <Trash2Icon className="size-4" />,
                                onSelect: async ({ modalId }) => {
                                    await modalStore.close(modalId);

                                    const result = await modalStore.confirm({
                                        message: '정말 삭제하시겠습니까?',
                                        confirmStyle: 'danger',
                                    });
                                    if (result.returnValue) {
                                        const deleted =
                                            await apiClient.post.member.deletePost.mutate({
                                                postHash: postHash!,
                                            });

                                        if (deleted) {
                                            if (webViewStore.isWebView) {
                                                await webViewStore.syncUi('postDeleted', {
                                                    channelType: channelType! as ChannelType,
                                                    postHash: postHash!,
                                                });
                                            } else {
                                                apiClient.post.public.getPostList.$invalidate({
                                                    channelType: channelType!,
                                                });
                                            }

                                            navigateBack();
                                        } else {
                                            modalStore.alert({
                                                message: '삭제에 실패했습니다.',
                                            });
                                        }
                                    }
                                },
                            },
                        ],
                    });
                }}
            />
            <Frame.Description>
                {postStore.post?.author?.name}
                {postStore.post?.publishedAt && (
                    <>
                        <span className="mx-1">•</span>
                        <CommonTimeFormat
                            time={postStore.post?.publishedAt}
                            isShowHour={true}
                            fromNow={true}
                        />
                    </>
                )}
            </Frame.Description>
            <Box isLoading={isLoading}>
                {postStore.post && (
                    <div className="prose max-w-none">
                        <MarkdownViewer markdown={postStore.post.content} />
                    </div>
                )}
            </Box>
        </Frame>
    );
});

const PostPage = observer(() => {
    const { channelType, postHash } = useParams();

    return (
        <ChannelProvider channelType={channelType as ChannelType}>
            <PostProvider channelType={channelType as ChannelType} postHash={postHash!}>
                <PostPageContent />
            </PostProvider>
        </ChannelProvider>
    );
});

export default PostPage;
